import React, { createContext, useContext, useState, useEffect } from 'react';
import { getMe, getProjects } from '../api';
import { defaultPermissions, normalizePermissions } from '../utils/permissions';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('user'));
      return saved ? { ...saved, permissions: normalizePermissions(saved.permissions) } : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      getMe().then(async res => {
        const nextUser = { ...res.data.user, permissions: normalizePermissions(res.data.user.permissions) };
        // Load project slugs before protected pages start sending API requests.
        await getProjects().catch(() => {});
        setUser(nextUser);
        localStorage.setItem('user', JSON.stringify(nextUser));
      }).catch(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const loginUser = async (token, userData) => {
    const nextUser = { ...userData, permissions: normalizePermissions(userData.permissions) };
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(nextUser));
    // A new login must load this account's projects before any page builds a
    // project URL. Otherwise the first link can briefly contain a database UUID.
    await getProjects().catch(() => {});
    setUser(nextUser);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const effectivePermissions = ['admin', 'sub_admin'].includes(user?.role)
    ? defaultPermissions(user.role)
    : {
        ...defaultPermissions(user?.role || 'viewer'),
        ...(user?.permissions || {}),
      };

  const hasPermission = (permission) => Boolean(effectivePermissions[permission]);

  const isAdmin = user?.role === 'admin';
  const isManager = ['admin', 'sub_admin', 'store_manager'].includes(user?.role);

  return (
    <AuthContext.Provider value={{ user, loading, loginUser, logout, isAdmin, isManager, hasPermission, permissions: effectivePermissions }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
